import React from 'react';
import * as d3 from 'd3';
import { useI18n } from '@whizz/react-i18n';
import type { ClassReportType, HighlightedProps } from '../../types';
import { SectionLayout } from '../modules';
import { StudentProgress } from '../../../components';
import { FIXED_AXIS } from '../../../components/charts/helpers/vars';

type Props = {
  classReport: ClassReportType;
  showOthersData: boolean
  hideAverage?: boolean
  chartIsExpanded?: boolean
} & HighlightedProps;

const QualityLearningTime = ({
  classReport,
  showOthersData,
  highlightedLabel,
  highlightedIds,
  hideAverage,
  chartIsExpanded,
  onElementClick,
}: Props): JSX.Element => {
  const { translate } = useI18n();

  const getHeaderValue = (): string => {
    let studentListWithZeroMathsAgeRemoved = classReport.students.all.filter((student) => student.chartsData.mathsAge > 0);
    if (highlightedLabel) {
      studentListWithZeroMathsAgeRemoved = studentListWithZeroMathsAgeRemoved.filter((student) => highlightedIds.includes(student.id));
    }
    const mean = d3.mean(studentListWithZeroMathsAgeRemoved.map((student) => student.chartsData.qualityLearningTime));
    return mean != undefined ? roundToDecimal(mean) : '';
  };

  const roundToDecimal = (value: number): string => {
    const decimal = Number.isSafeInteger(value) ? '.0f' : '.2f';
    return d3.format(decimal)(value);
  };

  const value = roundToDecimal(classReport.qualityLearningTimeAverage);
  const highlightValue = getHeaderValue();

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.QUALITY.title')}
      value={showOthersData ? value : null}
      highlightLabel={highlightedLabel}
      highlightValue={highlightValue}
      unit={translate('pages.REPORTS.SECTIONS.QUALITY.unit', {
        count: parseInt(value),
      })}
      description={translate('pages.REPORTS.SECTIONS.QUALITY.description')}
      moreInfo={translate('pages.REPORTS.SECTIONS.QUALITY.info')}
      className={'pdf-no-break'}
      data-cy="quality-learning-time"
      hideAverage={hideAverage}
      chartIsExpanded={chartIsExpanded}
      id='quality-learning-time-container'
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={classReport.students.all ?? []}
        chartType={'qualityLearningTime'}
        highlightedIds={highlightedIds}
        removeUnassessed={true}
        customAxis={FIXED_AXIS.recent?.qualityLearningTime}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

QualityLearningTime.displayName = 'QualityLearningTime';

export default QualityLearningTime;
